/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const INITIALIZE_PROTOCOL_CONFIG_DISCRIMINATOR = new Uint8Array([
  28, 50, 43, 233, 244, 98, 123, 118,
]);

export function getInitializeProtocolConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_PROTOCOL_CONFIG_DISCRIMINATOR,
  );
}

export type InitializeProtocolConfigInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountTreasury extends string | AccountMeta<string> = string,
  TAccountBuybackPool extends string | AccountMeta<string> = string,
  TAccountModeratorPool extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountTreasury extends string
        ? ReadonlyAccount<TAccountTreasury>
        : TAccountTreasury,
      TAccountBuybackPool extends string
        ? ReadonlyAccount<TAccountBuybackPool>
        : TAccountBuybackPool,
      TAccountModeratorPool extends string
        ? ReadonlyAccount<TAccountModeratorPool>
        : TAccountModeratorPool,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeProtocolConfigInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type InitializeProtocolConfigInstructionDataArgs = {};

export function getInitializeProtocolConfigInstructionDataEncoder(): FixedSizeEncoder<InitializeProtocolConfigInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_PROTOCOL_CONFIG_DISCRIMINATOR,
    }),
  );
}

export function getInitializeProtocolConfigInstructionDataDecoder(): FixedSizeDecoder<InitializeProtocolConfigInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getInitializeProtocolConfigInstructionDataCodec(): FixedSizeCodec<
  InitializeProtocolConfigInstructionDataArgs,
  InitializeProtocolConfigInstructionData
> {
  return combineCodec(
    getInitializeProtocolConfigInstructionDataEncoder(),
    getInitializeProtocolConfigInstructionDataDecoder(),
  );
}

export type InitializeProtocolConfigAsyncInput<
  TAccountConfig extends string = string,
  TAccountAuthority extends string = string,
  TAccountTreasury extends string = string,
  TAccountBuybackPool extends string = string,
  TAccountModeratorPool extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  config?: Address<TAccountConfig>;
  /** Authority who will control the config (typically DAO or multisig) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Treasury wallet to receive protocol fees */
  treasury: Address<TAccountTreasury>;
  /** Buyback pool wallet for token buybacks */
  buybackPool: Address<TAccountBuybackPool>;
  /** Moderator pool for dispute fees */
  moderatorPool: Address<TAccountModeratorPool>;
  systemProgram?: Address<TAccountSystemProgram>;
};

export async function getInitializeProtocolConfigInstructionAsync<
  TAccountConfig extends string,
  TAccountAuthority extends string,
  TAccountTreasury extends string,
  TAccountBuybackPool extends string,
  TAccountModeratorPool extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: InitializeProtocolConfigAsyncInput<
    TAccountConfig,
    TAccountAuthority,
    TAccountTreasury,
    TAccountBuybackPool,
    TAccountModeratorPool,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeProtocolConfigInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority,
    TAccountTreasury,
    TAccountBuybackPool,
    TAccountModeratorPool,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    treasury: { value: input.treasury ?? null, isWritable: false },
    buybackPool: { value: input.buybackPool ?? null, isWritable: false },
    moderatorPool: { value: input.moderatorPool ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            112, 114, 111, 116, 111, 99, 111, 108, 95, 99, 111, 110, 102, 105,
            103,
          ]),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.config),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.treasury),
      getAccountMeta(accounts.buybackPool),
      getAccountMeta(accounts.moderatorPool),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getInitializeProtocolConfigInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeProtocolConfigInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority,
    TAccountTreasury,
    TAccountBuybackPool,
    TAccountModeratorPool,
    TAccountSystemProgram
  >);
}

export type InitializeProtocolConfigInput<
  TAccountConfig extends string = string,
  TAccountAuthority extends string = string,
  TAccountTreasury extends string = string,
  TAccountBuybackPool extends string = string,
  TAccountModeratorPool extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  config: Address<TAccountConfig>;
  /** Authority who will control the config (typically DAO or multisig) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Treasury wallet to receive protocol fees */
  treasury: Address<TAccountTreasury>;
  /** Buyback pool wallet for token buybacks */
  buybackPool: Address<TAccountBuybackPool>;
  /** Moderator pool for dispute fees */
  moderatorPool: Address<TAccountModeratorPool>;
  systemProgram?: Address<TAccountSystemProgram>;
};

export function getInitializeProtocolConfigInstruction<
  TAccountConfig extends string,
  TAccountAuthority extends string,
  TAccountTreasury extends string,
  TAccountBuybackPool extends string,
  TAccountModeratorPool extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: InitializeProtocolConfigInput<
    TAccountConfig,
    TAccountAuthority,
    TAccountTreasury,
    TAccountBuybackPool,
    TAccountModeratorPool,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeProtocolConfigInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountAuthority,
  TAccountTreasury,
  TAccountBuybackPool,
  TAccountModeratorPool,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    treasury: { value: input.treasury ?? null, isWritable: false },
    buybackPool: { value: input.buybackPool ?? null, isWritable: false },
    moderatorPool: { value: input.moderatorPool ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.config),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.treasury),
      getAccountMeta(accounts.buybackPool),
      getAccountMeta(accounts.moderatorPool),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getInitializeProtocolConfigInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeProtocolConfigInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority,
    TAccountTreasury,
    TAccountBuybackPool,
    TAccountModeratorPool,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeProtocolConfigInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    /** Authority who will control the config (typically DAO or multisig) */
    authority: TAccountMetas[1];
    /** Treasury wallet to receive protocol fees */
    treasury: TAccountMetas[2];
    /** Buyback pool wallet for token buybacks */
    buybackPool: TAccountMetas[3];
    /** Moderator pool for dispute fees */
    moderatorPool: TAccountMetas[4];
    systemProgram: TAccountMetas[5];
  };
  data: InitializeProtocolConfigInstructionData;
};

export function parseInitializeProtocolConfigInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeProtocolConfigInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      authority: getNextAccount(),
      treasury: getNextAccount(),
      buybackPool: getNextAccount(),
      moderatorPool: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeProtocolConfigInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
